"use client"

import { useState, useEffect } from "react"
import { RecommendationBlockContent, Theme, defaultTheme, getPlainText } from "@/types"
import { Loader2 } from "lucide-react"
import { ToastPortal } from "../ToastPortal"
import { RichTextRenderer } from "../RichTextRenderer"

interface RecommendationBlockProps {
  content: RecommendationBlockContent
  onAnswer?: (answer: any) => void
  answer?: any
  allAnswers?: Record<string, any>
  isShopifyProxy?: boolean
  shopDomain?: string | null
  projectId?: string
  onCtaClick?: (stepId?: string, metadata?: any) => void
  mode?: "desktop" | "mobile"
  theme?: Theme | null
  // Shopify embed-specific cart functions
  getProduct?: (handle: string) => Promise<any>
  addToCart?: (variantId: number, quantity?: number, properties?: Record<string, string>) => Promise<any>
  onAddedToCart?: () => void
}

interface ShopifyProduct {
  id: number
  title: string
  handle: string
  description: string
  price: number
  compare_at_price: number | null
  available: boolean
  images: string[]
  variants: Array<{
    id: number
    title: string
    price: number
    available: boolean
  }>
  featured_image: string
}

export function RecommendationBlock({ 
  content, 
  onAnswer, 
  allAnswers = {},
  isShopifyProxy = false,
  shopDomain = null,
  projectId,
  onCtaClick,
  mode = "desktop",
  theme = null,
  getProduct,
  addToCart,
  onAddedToCart
}: RecommendationBlockProps) {
  const [productsData, setProductsData] = useState<ShopifyProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [addingToCart, setAddingToCart] = useState<Record<string, boolean>>({})
  const [cartItemCount, setCartItemCount] = useState(0)
  const [cartSuccessMessage, setCartSuccessMessage] = useState<string | null>(null)
  const [lastAddedProduct, setLastAddedProduct] = useState<string | null>(null)

  // Use provided theme or fallback to default
  const activeTheme = theme || defaultTheme

  // Update success message when cart count changes
  useEffect(() => {
    if (cartSuccessMessage && lastAddedProduct && cartItemCount > 0) {
      const itemText = cartItemCount === 1 ? 'item' : 'items'
      setCartSuccessMessage(`${lastAddedProduct} added! ${cartItemCount} ${itemText} in cart`)
    }
  }, [cartItemCount, lastAddedProduct, cartSuccessMessage])

  // Normalize content to handle both old and new formats
  const normalizeToArray = (value: any): string[] => {
    if (!value) return []
    if (Array.isArray(value)) {
      // Check if array contains ProductInfo objects or strings
      return value.map(item => {
        if (typeof item === 'string') return item
        if (typeof item === 'object' && item !== null && 'handle' in item) {
          return item.handle as string
        }
        // Fallback: try to extract any string property that looks like a handle
        if (typeof item === 'object' && item !== null) {
          return item.handle || item.productHandle || String(item)
        }
        return String(item)
      }).filter(handle => handle && typeof handle === 'string')
    }
    if (typeof value === 'string') return [value] // Old format compatibility
    return []
  }

  // Migrate old content format to new format
  const migrateContent = (oldContent: RecommendationBlockContent): RecommendationBlockContent => {
    // If products field doesn't exist but we have old data, migrate it
    if (!oldContent.products && oldContent.questionId) {
      return {
        products: normalizeToArray(
          oldContent.fallbackHandles || oldContent.fallbackProducts || (oldContent as any).fallbackHandle || []
        ),
        enableMapping: true, // Old blocks had mapping enabled
        questionId: oldContent.questionId,
        productMap: oldContent.productMap,
        headingText: oldContent.headingText,
        shopDomain: oldContent.shopDomain
      }
    }
    return oldContent
  }

  const migratedContent = migrateContent(content)
  
  // Get shop domain - prefer content.shopDomain, fallback to prop shopDomain
  const currentShopDomain = migratedContent.shopDomain || shopDomain

  // NEW LOGIC: Determine which products to show
  // Priority: 1) Answer-based mapping (if enabled), 2) Default products
  let productHandles: string[] = []
  
  if (migratedContent.enableMapping && migratedContent.questionId && migratedContent.productMap) {
    // Try answer-based mapping first
    const questionAnswer = allAnswers[migratedContent.questionId]
    
    if (questionAnswer && migratedContent.productMap[questionAnswer]) {
      const mappedProducts = normalizeToArray(migratedContent.productMap[questionAnswer])
      if (mappedProducts.length > 0) {
        productHandles = mappedProducts
      }
    }
  }
  
  // If no mapped products found (or mapping disabled), use default products
  if (productHandles.length === 0) {
    productHandles = normalizeToArray(migratedContent.products)
  }

  // Helper function to get enhanced attribution attributes
  const getAttributionAttributes = (): Record<string, string> => {
    if (!projectId) return {}
    
    const attributes: Record<string, string> = {
      quiz_funnel_project_id: projectId
    }
    
    const sessionId = localStorage.getItem('qf_session_id') || sessionStorage.getItem('qf_session_id')
    if (sessionId) {
      attributes.quiz_funnel_session_id = sessionId
    }
    
    return attributes
  }

  const getProductUrl = (handle: string) => {
    if (!handle) return null
    
    let baseUrl = ''
    if (isShopifyProxy) {
      baseUrl = `/products/${handle}`
    } else if (currentShopDomain) {
      baseUrl = `https://${currentShopDomain}/products/${handle}`
    } else {
      return null
    }
    
    // Enhanced attribution tracking with session ID
    if (projectId) {
      const sessionId = localStorage.getItem('qf_session_id') || sessionStorage.getItem('qf_session_id')
      const params = new URLSearchParams()
      params.set('qf_project', projectId)
      if (sessionId) {
        params.set('qf_session', sessionId)
      }
      
      const separator = baseUrl.includes('?') ? '&' : '?'
      return `${baseUrl}${separator}${params.toString()}`
    }
    
    return baseUrl
  }

  // Fetch current cart count
  useEffect(() => {
    const fetchCartCount = async () => {
      if (!currentShopDomain) return // Skip if no shop domain
      
      try {
        const isEmbedded = window !== window.top
        const isSameDomain = isShopifyProxy || 
          window.location.hostname.includes(currentShopDomain.replace('.myshopify.com', '')) ||
          (isEmbedded && currentShopDomain && window.location.href.includes('shopify'))
        
        // Try direct cart API first
        try {
          const response = await fetch('/cart.js')
          if (response.ok) {
            const cart = await response.json()
            setCartItemCount(cart.item_count || 0)
            return // Success, exit early
          }
        } catch (directError) {
          console.log('Direct cart fetch failed, trying proxy:', directError)
        }
        
        // Fallback to proxy API
        const response = await fetch(`/api/shopify/cart?shop=${encodeURIComponent(currentShopDomain)}`)
        if (response.ok) {
          const cart = await response.json()
          setCartItemCount(cart.item_count || 0)
        }
      } catch (err) {
        console.error('Failed to fetch cart:', err)
        // Don't show error to user for cart count - it's not critical
      }
    }
    
    fetchCartCount()
  }, [currentShopDomain, isShopifyProxy])

  // Function to refresh cart count
  const refreshCartCount = async () => {
    if (!currentShopDomain) return
    
    try {
      const isEmbedded = window !== window.top
      const isSameDomain = isShopifyProxy || 
        window.location.hostname.includes(currentShopDomain.replace('.myshopify.com', '')) ||
        (isEmbedded && currentShopDomain && window.location.href.includes('shopify'))
      
      // Try direct cart API first
      try {
        const response = await fetch('/cart.js')
        if (response.ok) {
          const cart = await response.json()
          setCartItemCount(cart.item_count || 0)
          return
        }
      } catch (directError) {
        console.log('Direct cart fetch failed during refresh')
      }
      
      // Fallback to proxy API
      const response = await fetch(`/api/shopify/cart?shop=${encodeURIComponent(currentShopDomain)}`)
      if (response.ok) {
        const cart = await response.json()
        setCartItemCount(cart.item_count || 0)
      }
    } catch (err) {
      console.error('Failed to refresh cart count:', err)
    }
  }

  // Fetch multiple products data from Shopify
  useEffect(() => {
    if (!productHandles || productHandles.length === 0) {
      setLoading(false)
      setProductsData([])
      return
    }

    const fetchProductsData = async () => {
      setLoading(true)
      setError(null)

      try {
        // Require shop domain for product fetching
        if (!currentShopDomain) {
          setError('Shop domain is required to fetch product details')
          setLoading(false)
          return
        }

        // Fetch all products in parallel
        const productPromises = productHandles.map(async (handle) => {
          let productJsonUrl = ''
          
          if (isShopifyProxy) {
            // Running in Shopify proxy - direct fetch from same origin
            productJsonUrl = `/products/${handle}.js`
          } else {
            // Use server-side proxy with shop domain for public API access
            productJsonUrl = `/api/preview/shopify/product?handle=${handle}&shop=${encodeURIComponent(currentShopDomain)}`
          }

          const response = await fetch(productJsonUrl)
          
          if (!response.ok) {
            if (response.status === 400) {
              const errorData = await response.json()
              if (errorData.needsConnection) {
                throw new Error('Connect your Shopify store to preview product details')
              }
            }
            console.warn(`Product not found: ${handle}`)
            return null
          }

          const data = await response.json()
          
          return {
            id: data.id,
            title: data.title,
            handle: data.handle,
            description: data.description || '',
            price: data.variants?.[0]?.price || data.price || 0,
            compare_at_price: data.variants?.[0]?.compare_at_price || data.compare_at_price || null,
            available: data.available,
            images: data.images || [],
            variants: data.variants || [],
            featured_image: data.featured_image || data.images?.[0] || ''
          } as ShopifyProduct
        })

        const products = await Promise.all(productPromises)
        const validProducts = products.filter((p): p is ShopifyProduct => p !== null)
        
        setProductsData(validProducts)
        
        if (validProducts.length === 0) {
          setError('No valid products found')
        }
      } catch (err) {
        console.error('Failed to fetch products:', err)
        setError(err instanceof Error ? err.message : 'Unable to load product details')
      } finally {
        setLoading(false)
      }
    }

    fetchProductsData()
  }, [productHandles.join(','), isShopifyProxy, currentShopDomain])

  // Cross-domain cart add using iframe to avoid page navigation
  const addToCartViaIframe = (shopDomain: string, variantId: number, quantity: number, attributes: Record<string, string> = {}): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        // Create unique iframe name to avoid conflicts
        const iframeName = `cart-iframe-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        
        // Create hidden iframe
        const iframe = document.createElement('iframe')
        iframe.name = iframeName
        iframe.style.display = 'none'
        iframe.style.width = '0'
        iframe.style.height = '0'
        iframe.style.border = 'none'
        iframe.style.visibility = 'hidden'
        iframe.style.position = 'absolute'
        iframe.style.left = '-9999px'
        
        // Create form that targets the iframe
        const form = document.createElement('form')
        form.method = 'POST'
        form.action = `https://${shopDomain}/cart/add`
        form.target = iframeName
        form.style.display = 'none'

        // Add variant ID input
        const idInput = document.createElement('input')
        idInput.type = 'hidden'
        idInput.name = 'id'
        idInput.value = variantId.toString()
        form.appendChild(idInput)

        // Add quantity input
        const quantityInput = document.createElement('input')
        quantityInput.type = 'hidden'
        quantityInput.name = 'quantity'
        quantityInput.value = quantity.toString()
        form.appendChild(quantityInput)

        // Add attributes if provided
        Object.entries(attributes).forEach(([key, value]) => {
          const attrInput = document.createElement('input')
          attrInput.type = 'hidden'
          attrInput.name = `attributes[${key}]`
          attrInput.value = value
          form.appendChild(attrInput)
        })

        // Handle iframe load events
        let resolved = false
        const cleanup = () => {
          if (!resolved) {
            resolved = true
            try {
              document.body.removeChild(iframe)
              document.body.removeChild(form)
            } catch (e) {
              // Elements might already be removed
            }
          }
        }

        // Set up timeout to avoid hanging
        const timeout = setTimeout(() => {
          if (!resolved) {
            cleanup()
            // Don't reject immediately - assume success for better UX
            resolve()
          }
        }, 8000) // 8 second timeout

        // Handle iframe load (both success and error cases)
        iframe.onload = () => {
          if (!resolved) {
            clearTimeout(timeout)
            cleanup()
            resolve()
          }
        }

        iframe.onerror = () => {
          if (!resolved) {
            clearTimeout(timeout)
            cleanup()
            // Still resolve for better UX - the form submission likely succeeded
            resolve()
          }
        }

        // Append iframe and form to body, then submit
        document.body.appendChild(iframe)
        document.body.appendChild(form)
        
        // Submit form after a brief delay to ensure iframe is ready
        setTimeout(() => {
          try {
            form.submit()
          } catch (error) {
            if (!resolved) {
              clearTimeout(timeout)
              cleanup()
              reject(error)
            }
          }
        }, 100)

      } catch (error) {
        reject(error)
      }
    })
  }

  // Helper function to notify Shopify theme about cart updates
  const notifyThemeCartUpdated = (variantId: number, quantity: number, productData?: any) => {
    // Dispatch multiple standard Shopify cart events for theme compatibility
    const events = [
      'cart:refresh',
      'cart:updated',
      'cart.requestComplete',
      'theme:cart:change',
      'product:added',
      'ajaxCart:updated',
    ]
    
    events.forEach(eventName => {
      try {
        document.dispatchEvent(new CustomEvent(eventName, {
          bubbles: true,
          detail: { 
            variant_id: variantId, 
            quantity: quantity,
            product: productData 
          }
        }))
      } catch (e) {
        // Silently fail if event dispatch fails
      }
    })
    
    // Try common Shopify global refresh methods
    try { 
      if (typeof (window as any).Shopify?.theme?.cart?.refresh === 'function') {
        (window as any).Shopify.theme.cart.refresh()
      }
    } catch {}
    
    try { 
      if (typeof (window as any).theme?.cartUpdateCallback === 'function') {
        (window as any).theme.cartUpdateCallback()
      }
    } catch {}
    
    // jQuery-based themes
    try {
      if (typeof (window as any).$ === 'function') {
        (window as any).$(document.body).trigger('added.ajaxProduct')
      }
    } catch {}
  }

  const handleAddToCart = async (product: ShopifyProduct) => {
    if (!product || !product.variants[0] || !currentShopDomain) {
      console.error('Missing product, variant, or shop domain')
      return
    }

    setAddingToCart(prev => ({ ...prev, [product.handle]: true }))

    try {
      // If custom addToCart function is provided (from embed), use it
      if (addToCart && typeof addToCart === 'function') {
        await addToCart(
          product.variants[0].id,
          1,
          getAttributionAttributes()
        )
        
        // Show success feedback
        const newCount = cartItemCount + 1
        setCartItemCount(newCount)
        setLastAddedProduct(product.title)
        const itemText = newCount === 1 ? 'item' : 'items'
        setCartSuccessMessage(`${product.title} added! ${newCount} ${itemText} in cart`)
        
        // Notify theme to update cart UI
        notifyThemeCartUpdated(product.variants[0].id, 1, product)
        
        // Refresh cart count
        setTimeout(() => refreshCartCount(), 1000)
        
        // Message remains visible (no auto-dismiss)
        
        // Track the action
        onAnswer?.("added_to_cart")
        const questionAnswer = migratedContent.enableMapping && migratedContent.questionId 
          ? allAnswers[migratedContent.questionId] 
          : undefined
        const usedMapping = Boolean(
          migratedContent.enableMapping && 
          migratedContent.questionId && 
          questionAnswer !== undefined &&
          migratedContent.productMap &&
          migratedContent.productMap[String(questionAnswer)]?.length > 0
        )
        onCtaClick?.(undefined, {
          ctaType: 'recommendation_add_to_cart',
          questionId: migratedContent.questionId,
          questionAnswer,
          usedMapping,
          productHandle: product.handle,
          productId: product.id,
          variantId: product.variants[0].id,
          shopDomain: currentShopDomain,
        })
        
        // Call onAddedToCart callback if provided
        if (onAddedToCart && typeof onAddedToCart === 'function') {
          onAddedToCart()
        }
        
        return // Exit early, custom function handled everything
      }
      
      // Check if we're on the same domain as the Shopify store or embedded in it
      const isEmbedded = window !== window.top // Check if we're in an iframe
      const isSameDomain = isShopifyProxy || 
        window.location.hostname.includes(currentShopDomain.replace('.myshopify.com', '')) ||
        (isEmbedded && currentShopDomain && window.location.href.includes('shopify'))
      
      // Debug: log cart context in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Cart context:', { isShopifyProxy, isEmbedded, isSameDomain, currentShopDomain, hostname: window.location.hostname })
      }

      if (isSameDomain) {
        // Same domain or embedded - try direct APIs first, fallback to iframe
        try {
          const attributes = getAttributionAttributes()
          
          if (Object.keys(attributes).length > 0) {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ attributes })
            })
          }

          const addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: product.variants[0].id,
              quantity: 1
            })
          })

          if (!addResponse.ok) {
            throw new Error('Direct API failed, trying iframe method')
          }

          const newCount = cartItemCount + 1
          setCartItemCount(newCount)
          setLastAddedProduct(product.title)
          const itemText = newCount === 1 ? 'item' : 'items'
          setCartSuccessMessage(`${product.title} added! ${newCount} ${itemText} in cart`)
          
          // Notify theme to update cart UI
          notifyThemeCartUpdated(product.variants[0].id, 1, product)
        } catch (apiError) {
          console.log('Direct API failed, using iframe fallback:', apiError)
          // Fallback to iframe method even on same domain
          await addToCartViaIframe(
            currentShopDomain, 
            product.variants[0].id, 
            1, 
            getAttributionAttributes()
          )
          const newCount = cartItemCount + 1
          setCartItemCount(newCount)
          setLastAddedProduct(product.title)
          const itemText = newCount === 1 ? 'item' : 'items'
          setCartSuccessMessage(`${product.title} added! ${newCount} ${itemText} in cart`)
          
          // Notify theme to update cart UI
          notifyThemeCartUpdated(product.variants[0].id, 1, product)
        }
      } else {
        // Cross-domain - use iframe method to add to cart (stays on page)
        await addToCartViaIframe(
          currentShopDomain, 
          product.variants[0].id, 
          1, 
          getAttributionAttributes()
        )
        
        // Update cart count optimistically
        const newCount = cartItemCount + 1
        setCartItemCount(newCount)
        setLastAddedProduct(product.title)
        const itemText = newCount === 1 ? 'item' : 'items'
        
        // Show success feedback
        setCartSuccessMessage(`${product.title} added! ${newCount} ${itemText} in cart`)
        
        // Notify theme to update cart UI
        notifyThemeCartUpdated(product.variants[0].id, 1)
      }

      // Refresh cart count after a short delay to ensure the addition was processed
      setTimeout(() => {
        refreshCartCount()
      }, 1000)

      // Message remains visible (no auto-dismiss)

      // Track the action
      onAnswer?.("added_to_cart")
      const questionAnswer = migratedContent.enableMapping && migratedContent.questionId 
        ? allAnswers[migratedContent.questionId] 
        : undefined
      const usedMapping = Boolean(
        migratedContent.enableMapping && 
        migratedContent.questionId && 
        questionAnswer !== undefined &&
        migratedContent.productMap &&
        migratedContent.productMap[String(questionAnswer)]?.length > 0
      )
      onCtaClick?.(undefined, {
        ctaType: 'recommendation_add_to_cart',
        questionId: migratedContent.questionId,
        questionAnswer,
        usedMapping,
        productHandle: product.handle,
        productId: product.id,
        variantId: product.variants[0].id,
        shopDomain: currentShopDomain,
      })

    } catch (err) {
      console.error('Failed to add to cart:', err)
      
      // Fallback: redirect to product page with add to cart parameters
      const productUrl = getProductUrl(product.handle)
      if (productUrl) {
        const addToCartUrl = `${productUrl}?variant=${product.variants[0].id}&quantity=1`
        const attributionParam = projectId ? `&qf_project=${projectId}` : ''
        window.open(`${addToCartUrl}${attributionParam}`, '_blank')
      } else {
        alert('Unable to add product to cart. Please try clicking the "View Product" button instead.')
      }
    } finally {
      setAddingToCart(prev => ({ ...prev, [product.handle]: false }))
    }
  }

  const handleViewProduct = (handle: string) => {
    const productUrl = getProductUrl(handle)
    if (!productUrl) return

    // Track view product click
    const questionAnswer = migratedContent.enableMapping && migratedContent.questionId 
      ? allAnswers[migratedContent.questionId] 
      : undefined
    const usedMapping = Boolean(
      migratedContent.enableMapping && 
      migratedContent.questionId && 
      questionAnswer !== undefined &&
      migratedContent.productMap &&
      migratedContent.productMap[String(questionAnswer)]?.length > 0
    )
    onCtaClick?.(undefined, {
      ctaType: 'recommendation_view_product',
      questionId: migratedContent.questionId,
      questionAnswer,
      usedMapping,
      productHandle: handle,
      targetUrl: productUrl,
      isShopifyProxy,
      shopDomain,
    })

    // Navigate to product page
    if (isShopifyProxy || shopDomain) {
      window.location.href = productUrl
    } else {
      window.open(productUrl, '_blank')
    }
  }


  // Format price (Shopify Ajax API always returns prices in cents/minor units)
  const formatPrice = (price: number | string) => {
    const priceNum = typeof price === 'string' ? parseFloat(price) : price
    
    // Always convert from cents to dollars
    return `$${(priceNum / 100).toFixed(2)}`
  }

  if (!productHandles || productHandles.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">No product recommendations available</p>
      </div>
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className="p-6 flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mb-3" />
        <p className="text-gray-600 text-sm">Loading your recommendations...</p>
      </div>
    )
  }

  // Error state
  if (error || productsData.length === 0) {
    const isConnectionError = error?.includes('Connect your Shopify store')
    
    return (
      <div className="p-6">
        <div className={`border rounded-lg p-4 text-center ${
          isConnectionError 
            ? 'bg-blue-50 border-blue-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <p className={`text-sm mb-3 font-medium ${
            isConnectionError ? 'text-blue-800' : 'text-red-800'
          }`}>
            {error || 'Unable to load product details'}
          </p>
          {isConnectionError && (
            <a
              href="/dashboard/settings"
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Connect Shopify Store
            </a>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <h3 
        className="text-xl font-semibold text-center mb-4"
        style={{ 
          color: activeTheme.colors.text,
          fontFamily: activeTheme.fonts.heading
        }}
      >
        <RichTextRenderer content={content.headingText || "Based on your answer, we recommend:"} />
      </h3>
      
      {/* Product Grid - Centered horizontal layout with max 3 per row */}
      <div className={`flex flex-wrap justify-center gap-4 mb-6 max-w-6xl mx-auto ${mode === "mobile" ? "px-4" : ""}`}>
        {productsData.map((product) => (
          <div 
            key={product.handle}
            className={`bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col h-full min-h-[420px] ${
              mode === "mobile" 
                ? "w-full max-w-sm" // Mobile: single column, centered
                : productsData.length === 1 
                  ? "w-80 max-w-sm" // 1 product: fixed width for consistency
                  : productsData.length === 2 
                    ? "w-80" // 2 products: fixed width
                    : "w-80" // 3+ products: consistent width
            }`}
          >
            {/* Product Image */}
            {product.featured_image && (
              <div className="aspect-square w-full bg-gray-100 overflow-hidden">
                <img
                  src={product.featured_image}
                  alt={product.title}
                  className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => handleViewProduct(product.handle)}
                />
              </div>
            )}
            
            {/* Product Info */}
            <div className="p-4 flex flex-col h-full">
              {/* Title section with fixed height for consistency */}
              <div className="h-12 mb-2 flex items-start">
                <h4 
                  className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-indigo-600 transition-colors line-clamp-2 leading-tight"
                  onClick={() => handleViewProduct(product.handle)}
                  title={product.title}
                >
                  {product.title}
                </h4>
              </div>
              
              {/* Price section with fixed height */}
              <div className="h-8 mb-3 flex items-center">
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold text-gray-900">
                    {formatPrice(product.price)}
                  </span>
                  {product.compare_at_price && product.compare_at_price > product.price && (
                    <span className="text-xs text-gray-500 line-through">
                      {formatPrice(product.compare_at_price)}
                    </span>
                  )}
                </div>
              </div>

              {/* Availability section - only height when needed */}
              {!product.available && (
                <div className="mb-3">
                  <div className="bg-yellow-50 border border-yellow-200 rounded px-3 py-1.5">
                    <p className="text-yellow-800 text-xs">
                      Out of stock
                    </p>
                  </div>
                </div>
              )}

              {/* Spacer to push button to bottom */}
              <div className="flex-grow" style={{ minHeight: '20px' }}></div>

              {/* Add to Cart Button */}
              <button
                onClick={() => handleAddToCart(product)}
                disabled={!product.available || addingToCart[product.handle]}
                className={`w-full flex items-center justify-center px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                  !product.available
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : addingToCart[product.handle]
                    ? "cursor-wait"
                    : ""
                }`}
                style={{
                  backgroundColor: !product.available 
                    ? undefined
                    : addingToCart[product.handle]
                      ? activeTheme.colors.secondary || activeTheme.colors.primary
                      : activeTheme.colors.primary,
                  color: !product.available 
                    ? undefined
                    : activeTheme.colors.buttonText
                }}
              >
                {addingToCart[product.handle] ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Add to Cart"
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Success Message - using ToastPortal for viewport positioning */}
      {cartSuccessMessage && (
        <ToastPortal>
          <div 
            style={{
              backgroundColor: activeTheme.colors.primary,
              color: activeTheme.colors.buttonText,
              padding: '12px 24px',
              borderRadius: '8px',
              boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
              maxWidth: '90vw',
              fontWeight: 500,
              textAlign: 'center',
              pointerEvents: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}
          >
            <span>{cartSuccessMessage}</span>
            <a
              href={isShopifyProxy || currentShopDomain ? '/cart' : `https://${currentShopDomain}/cart`}
              style={{
                color: activeTheme.colors.buttonText,
                textDecoration: 'underline',
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
            >
              View Cart
            </a>
          </div>
        </ToastPortal>
      )}

      {/* Cart Summary with View Cart button */}
      {cartItemCount > 0 && (
        <div className="border-t border-gray-200 pt-6">
          <div 
            className="rounded-lg p-4 text-center"
            style={{
              backgroundColor: `${activeTheme.colors.primary}15`, // 15 = ~8% opacity in hex
              border: `1px solid ${activeTheme.colors.primary}40` // 40 = ~25% opacity in hex
            }}
          >
            <p 
              className="font-medium mb-4"
              style={{
                color: activeTheme.colors.primary
              }}
            >
              🛒 {cartItemCount} {cartItemCount === 1 ? 'item' : 'items'} in your cart
            </p>
            <a
              href={isShopifyProxy || currentShopDomain ? '/cart' : `https://${currentShopDomain}/cart`}
              className="inline-block px-6 py-3 rounded-full font-medium text-white transition-all shadow-lg hover:shadow-xl"
              style={{
                backgroundColor: activeTheme.colors.primary,
                color: activeTheme.colors.buttonText
              }}
            >
              View Cart
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

